import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { Tables, TablesUpdate } from "../../../database.types";
import { Link } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import {
  AutoSaveIndicator,
  AutoSaveStatus,
} from "../../components/AutoSaveIndicator";
import { StandardAutocomplete } from "../../components/StandardAutocomplete";
import { StandardTextInput } from "../../components/StandardTextInput";
import { useSupabase } from "../../auth/useSupabase";
import { gameService } from "../../data/gameService";
import { teamService } from "../../data/teamService";
import { debounce } from "lodash";

const DEBOUNCE_MS = 400;

interface GameEditorProps {
  gameId: string;
  leagueId: string;
}

const GameEditor = (props: GameEditorProps) => {
  const { gameId, leagueId } = props;
  const { supabase } = useSupabase();
  const theme = useTheme();

  const [game, setGame] = useState<Tables<"games"> | null>(null);
  const [teams, setTeams] = useState<Tables<"teams">[]>([]);

  // Auto-save indicator
  const [autoSaveState, setAutoSaveState] = useState<AutoSaveStatus>("idle");
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const markSaving = useCallback(() => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    setAutoSaveState("saving");
  }, []);

  const markSaved = useCallback(() => {
    setAutoSaveState("saved");
    savedTimerRef.current = setTimeout(() => setAutoSaveState("idle"), 2000);
  }, []);

  const markError = useCallback(() => {
    setAutoSaveState("error");
    savedTimerRef.current = setTimeout(() => setAutoSaveState("idle"), 3000);
  }, []);

  // Fetch game and teams on mount
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [fetchedGame, fetchedTeams] = await Promise.all([
          gameService.getById(supabase, gameId),
          teamService.getAllLeagueTeams(supabase, leagueId),
        ]);
        if (cancelled) return;
        setGame(fetchedGame);
        setTeams(fetchedTeams);
      } catch (err) {
        console.error("Error loading game editor data:", err);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [supabase, gameId, leagueId]);

  // Accumulate pending updates so rapid changes to different fields all save
  const pendingUpdatesRef = useRef<TablesUpdate<"games">>({});

  const debouncedSave = useMemo(
    () =>
      debounce(async () => {
        const updates = { ...pendingUpdatesRef.current };
        pendingUpdatesRef.current = {};
        if (Object.keys(updates).length === 0) return;

        markSaving();
        try {
          await gameService.update(supabase, gameId, updates);
          markSaved();
        } catch {
          markError();
        }
      }, DEBOUNCE_MS),
    [supabase, gameId, markSaving, markSaved, markError],
  );

  // Cancel debounced save on unmount
  useEffect(() => {
    return () => {
      debouncedSave.flush();
    };
  }, [debouncedSave]);

  const handleNameChange = (name: string) => {
    setGame((prev) => (prev ? { ...prev, name } : prev));
    pendingUpdatesRef.current = { ...pendingUpdatesRef.current, name };
    debouncedSave();
  };

  const handleTeamOneChange = (teamId: string | null) => {
    setGame((prev) => (prev ? { ...prev, team_one_id: teamId } : prev));
    pendingUpdatesRef.current = {
      ...pendingUpdatesRef.current,
      team_one_id: teamId,
    };
    debouncedSave();
  };

  const handleTeamTwoChange = (teamId: string | null) => {
    setGame((prev) => (prev ? { ...prev, team_two_id: teamId } : prev));
    pendingUpdatesRef.current = {
      ...pendingUpdatesRef.current,
      team_two_id: teamId,
    };
    debouncedSave();
  };

  if (!game) return null;

  const teamOneValue = (() => {
    const v = teams.find((t) => t.id === game.team_one_id);
    return v ? { label: v.name, value: v.id } : undefined;
  })();
  const teamTwoValue = (() => {
    const v = teams.find((t) => t.id === game.team_two_id);
    return v ? { label: v.name, value: v.id } : undefined;
  })();

  return (
    <Box
      display="flex"
      gap="48px"
      bgcolor={theme.palette.primary.dark}
      border={`1px solid ${theme.palette.primary.main}`}
      alignItems="center"
      borderRadius="4px"
      padding="16px"
      overflow="auto"
    >
      <Link to={`/admin`}>
        <IconButton color="primary" size="large">
          <ArrowBack />
        </IconButton>
      </Link>
      {/* Team Selection */}
      <Box display="flex" gap="12px" alignItems="center">
        <StandardAutocomplete
          options={teams
            .filter((t) => t && t.id !== game.team_two_id)
            .map((t) => ({ label: t.name, value: t.id }))}
          value={teamOneValue}
          onChange={(_, val) => handleTeamOneChange(val?.value ?? null)}
          sx={{ width: "300px" }}
          data-testid="team-one-select"
        />
        <Typography color="primary">vs.</Typography>
        <StandardAutocomplete
          options={teams
            .filter((t) => t && t.id !== game.team_one_id)
            .map((t) => ({ label: t.name, value: t.id }))}
          value={teamTwoValue}
          onChange={(_, val) => handleTeamTwoChange(val?.value ?? null)}
          sx={{ width: "300px" }}
          data-testid="team-two-select"
        />
      </Box>

      <StandardTextInput
        value={game.name}
        onChange={(e) => handleNameChange(e.target.value)}
        label="Game Title"
        sx={{ width: "300px" }}
      />

      <AutoSaveIndicator status={autoSaveState} />
    </Box>
  );
};

export default GameEditor;
